import { useLanguage } from '@context/LanguageContext'
import { useDocumentMetadata } from '@hooks/useDocumentMetadata'
import StaticLayout from '../layout/StaticLayout'
import { useNamespaces } from '@hooks/useNamespaces'
import { ROUTE_NAMESPACES, LANGUAGES_WITH_LEGAL } from '@i18n/namespaces'
import ScreenFallback from '@components/layout/ScreenFallback'
import styles from './Privacy.module.css'
import noticeStyles from './legalNotice.module.css'

export default function Privacy () {
  const { t, lang } = useLanguage()
  const { isLoading: namespacesLoading } = useNamespaces(ROUTE_NAMESPACES.privacy)
  useDocumentMetadata()

  if (namespacesLoading) {
    return <ScreenFallback />
  }

  const showLegalNotice = !LANGUAGES_WITH_LEGAL.includes(lang)

  return (
    <StaticLayout>
      <div className={styles.privacyPage}>
        {showLegalNotice && (
          <div className={noticeStyles.notice} role='alert'>
            <p>{t.legal.notice}</p>
          </div>
        )}
        <header className={styles.header}>
          <h1 className={styles.title}>{t.privacy.title}</h1>
          <p className={styles.date}>{t.privacy.lastUpdated}</p>
        </header>

        <div className={styles.content}>
          <section className={styles.section}>
            <p className={styles.intro}>
              {t.privacy.intro.text} <strong>{t.privacy.intro.company}</strong> {t.privacy.intro.description}
            </p>

            <ul className={styles.servicesList}>
              <li>{t.privacy.intro.services.website}</li>
              <li>{t.privacy.intro.services.mobileApp}</li>
              <li>{t.privacy.intro.services.game}</li>
              <li>{t.privacy.intro.services.other}</li>
            </ul>

            <p className={styles.contact}>
              <strong>{t.privacy.intro.questions}</strong> {t.privacy.intro.contactText}
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>{t.privacy.summary.title}</h2>
            <p className={styles.summaryIntro}>{t.privacy.summary.intro}</p>
            <div className={styles.summaryPoints}>
              <div className={styles.point}>
                <h3 className={styles.pointTitle}>{t.privacy.summary.points.personalInfo.title}</h3>
                <p>{t.privacy.summary.points.personalInfo.text}</p>
              </div>

              <div className={styles.point}>
                <h3 className={styles.pointTitle}>{t.privacy.summary.points.sensitiveInfo.title}</h3>
                <p>{t.privacy.summary.points.sensitiveInfo.text}</p>
              </div>

              <div className={styles.point}>
                <h3 className={styles.pointTitle}>{t.privacy.summary.points.thirdParty.title}</h3>
                <p>{t.privacy.summary.points.thirdParty.text}</p>
              </div>

              <div className={styles.point}>
                <h3 className={styles.pointTitle}>{t.privacy.summary.points.processing.title}</h3>
                <p>{t.privacy.summary.points.processing.text}</p>
              </div>

              <div className={styles.point}>
                <h3 className={styles.pointTitle}>{t.privacy.summary.points.sharing.title}</h3>
                <p>{t.privacy.summary.points.sharing.text}</p>
              </div>

              <div className={styles.point}>
                <h3 className={styles.pointTitle}>{t.privacy.summary.points.security.title}</h3>
                <p>{t.privacy.summary.points.security.text}</p>
              </div>

              <div className={styles.point}>
                <h3 className={styles.pointTitle}>{t.privacy.summary.points.rights.title}</h3>
                <p>{t.privacy.summary.points.rights.text}</p>
              </div>

              <div className={styles.point}>
                <h3 className={styles.pointTitle}>{t.privacy.summary.points.exercise.title}</h3>
                <p>{t.privacy.summary.points.exercise.text}</p>
              </div>
            </div>

            <p className={styles.summaryConclusion}>{t.privacy.summary.conclusion}</p>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>{t.privacy.tableOfContents.title}</h2>
            <nav className={styles.toc}>
              {t.privacy.tableOfContents.items.map((item, index) => (
                <a key={index} href={item.href} className={styles.tocLink}>
                  {item.text}
                </a>
              ))}
            </nav>
          </section>

          {t.privacy.sections.map((section, index) => (
            <section key={index} className={styles.section} id={section.id}>
              <h2 className={styles.sectionTitle}>{section.title}</h2>

              {section.subsections?.map((subsection, subIndex) => (
                <div key={subIndex} className={styles.subsection}>
                  <h3 className={styles.subsectionTitle}>{subsection.title}</h3>
                  {subsection.content.map((content, contentIndex) => (
                    <div key={contentIndex} className={styles.contentBlock}>
                      {content.type === 'paragraph' && (
                        <p className={styles.paragraph}>{content.text}</p>
                      )}

                      {content.type === 'list' && (
                        <ul className={styles.list}>
                          {content.items.map((item, itemIndex) => (
                            <li key={itemIndex} className={styles.listItem}>
                              {item}
                            </li>
                          ))}
                        </ul>
                      )}

                      {content.type === 'table' && (
                        <>
                          <div className={styles.tableWrapper}>
                            <table className={styles.table}>
                              <thead>
                                <tr>
                                  {content.headers.map((header, headerIndex) => (
                                    <th key={headerIndex} className={styles.tableHeader}>
                                      {header}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {content.rows.map((row, rowIndex) => (
                                  <tr key={rowIndex} className={styles.tableRow}>
                                    {row.map((cell, cellIndex) => (
                                      <td key={cellIndex} className={styles.tableCell}>
                                        {cell}
                                      </td>
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </>
                      )}

                      {content.type === 'emphasis' && (
                        <p className={styles.emphasis}>{content.text}</p>
                      )}
                    </div>
                  ))}
                </div>
              ))}

              {!section.subsections && section.content.map((content, contentIndex) => (
                <div key={contentIndex} className={styles.contentBlock}>
                  {content.type === 'paragraph' && (
                    <p className={styles.paragraph}>{content.text}</p>
                  )}

                  {content.type === 'list' && (
                    <ul className={styles.list}>
                      {content.items.map((item, itemIndex) => (
                        <li key={itemIndex} className={styles.listItem}>
                          {item}
                        </li>
                      ))}
                    </ul>
                  )}

                  {content.type === 'table' && (
                    <>
                      <div className={styles.tableWrapper}>
                        <table className={styles.table}>
                          <thead>
                            <tr>
                              {content.headers.map((header, headerIndex) => (
                                <th key={headerIndex} className={styles.tableHeader}>
                                  {header}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {content.rows.map((row, rowIndex) => (
                              <tr key={rowIndex} className={styles.tableRow}>
                                {row.map((cell, cellIndex) => (
                                  <td key={cellIndex} className={styles.tableCell}>
                                    {cell}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}

                  {content.type === 'emphasis' && (
                    <p className={styles.emphasis}>{content.text}</p>
                  )}
                </div>
              ))}
            </section>
          ))}
        </div>
      </div>
    </StaticLayout>
  )
}
